import { Button, Row, Space, Spin, Typography } from "antd";
import React, { RefObject, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import BookmarkFillIcon from "../../assets/icons/bookmark-filled.svg";
import BookmarkIcon from "../../assets/icons/bookmark-white.svg";
import ChevronRight from "../../assets/icons/go-profile-chevron.svg";
import LikeGradientIcon from "../../assets/icons/like-gradient.svg";
import LikeIcon from "../../assets/icons/like.svg";
import VerifyIcon from "../../assets/icons/verify.svg";
import { CREATOR } from "../../helpers/constant";
import { updateBookmarkState, updateLikeState } from "../../redux/feeds/feedsSlice";
import { AppDispatch } from "../../redux/store";
import { authService, reactionService } from "../../services";
import AvatarComponent from "../AvatarComponent";
import CreatorDeletePostModal from "../CreatorDeletePostModal";
import FeedMediaComponent from "../MediaComponent/FeedMediaComponent";
import LikeAnimation from "../LikeAnimation";
import OptionsModalComponent from "../OptionsModalComponent";
import ReadMore from "../ReadMoreComponent";
import ReportModal from "../ReportModal";
import SpinnerComponent from "../SpinnerComponent/SpinnerComponent";
import ThreeDotsComponent from "../ThreeDotsComponent";
import "./index.less";

type props = {
  refProp: RefObject<HTMLDivElement> | null;
  feedId: string;
  name: string;
  username: string;
  totalLikes?: number;
  avatar: string;
  creatorId?: string;
  fileUrl?: string;
  thumbnail?: string;
  type: string;
  isLiked?: boolean;
  isBookMarked?: boolean;
  isPrivate?: boolean;
  description: string;
  verifiedAccount?: boolean;
  isCurrentUser: boolean;
  isFanHomePage?: boolean;
  isUploading?: boolean;
  isMyProfile?: boolean;
  creatorIds?: any;
};

const MainSwiperSlideItemV2: React.FC<props> = ({
  refProp,
  feedId,
  name,
  username,
  totalLikes,
  avatar,
  creatorId,
  fileUrl,
  thumbnail,
  type,
  isLiked,
  isBookMarked,
  isPrivate,
  description,
  verifiedAccount,
  isCurrentUser,
  isFanHomePage,
  isUploading,
  isMyProfile,
  creatorIds,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [likes, setLikes] = useState(isLiked);
  const [postTotalLikes, setPostTotalLikes] = useState<any>(totalLikes);
  const [bookmarks, setBookmark] = useState(isBookMarked);
  const [showLikeAnimation, setShowLikeAnimation] = useState(false);

  useEffect(() => {
    setBookmark(isBookMarked);
  }, [isBookMarked]);

  useEffect(() => {
    setLikes(isLiked);
  }, [isLiked]);

  useEffect(() => {
    setPostTotalLikes(totalLikes);
  }, [totalLikes]);

  async function onLikeClick() {
    const payload = {
      action: "like",
      objectId: feedId,
      objectType: "feed",
    };
    if (likes) {
      setLikes(false);
      setPostTotalLikes(postTotalLikes - 1);
      updateFeedLikeState(false);
      await reactionService.deactivatedReaction(payload);
    } else {
      setPostTotalLikes(postTotalLikes + 1);
      setLikes(true);
      updateFeedLikeState(true);
      setShowLikeAnimation(true);
      await reactionService.createReaction(payload);
    }
  }

  async function onBookmarkClick() {
    const payload = {
      action: "bookmark",
      objectId: feedId,
      objectType: "feed",
    };
    if (bookmarks) {
      setBookmark(false);
      updateFeedBookmarkState(false);
      await reactionService.deactivatedReaction(payload);
    } else {
      setBookmark(true);
      updateFeedBookmarkState(true);
      await reactionService.createReaction(payload);
    }
  }

  async function updateFeedBookmarkState(isBookmark: boolean) {
    dispatch(updateBookmarkState({ feedId, isBookmark, creatorId, isMyProfile }));
  }

  async function updateFeedLikeState(isLiked: boolean) {
    dispatch(updateLikeState({ feedId, isLiked, creatorId, isMyProfile }));
  }

  const renderBookmarkIcon = () => {
    if (isPrivate) {
      return null;
    }

    return (
      <img
        className="slideItemBookmark"
        src={bookmarks ? BookmarkFillIcon : BookmarkIcon}
        alt={bookmarks ? "bookmarkFill" : "bookmark"}
        onClick={onBookmarkClick}
      />
    );
  };

  const processDescription = (text: any, creatorIds: any) => {
    if (text === undefined || text === "") {
      return text;
    }
    const words = text.split(/\s+/);
    return words.map((word: any, index: any) => {
      if (word.startsWith("@") && word.length > 1) {
        const username = word.slice(1);
        const creator = creatorIds.find((creator: any) => creator.username === username);
        if (creator) {
          return (
            <strong key={index} onClick={() => navigate(`/p/${word.slice(1)}`)}>
              {word}{" "}
            </strong>
          );
        }
      }
      return word + " ";
    });
  };

  const closeReportModal = useCallback(() => {
    setIsReportModalOpen(false);
  }, []);

  const closeOptionsModal = useCallback(() => {
    setIsOptionsModalOpen(false);
  }, []);

  const onReportClick = useCallback(() => {
    setIsOptionsModalOpen(false);
    setIsReportModalOpen(true);
  }, []);

  const onOptionsClick = useCallback(() => {
    isCurrentUser ? setDeleteModalOpen(true) : setIsOptionsModalOpen(true);
  }, [isCurrentUser]);

  return (
    <div ref={refProp} className="main-content-wrapper">
      <div className="main-content-header mt-5">
        <Space>
          <AvatarComponent className="cursor-pointer" image={avatar} size={34} onClick={() => navigate(`/p/${username}`)} />
          <Typography.Text className="slideItemNameLabel cursor-pointer" onClick={() => navigate(`/p/${username}`)}>
            {username}
          </Typography.Text>
          {verifiedAccount === true && <img src={VerifyIcon} alt="verify" />}
        </Space>
        <ThreeDotsComponent className="cursor-pointer" onClick={onOptionsClick} />
      </div>
      <div className={"main-container-image mt-5"}>
        <Spin spinning={isUploading} className="feedSpinner" indicator={<SpinnerComponent />} />
        <div className="slideItemPostImg" onDoubleClick={onLikeClick}>
          <LikeAnimation trigger={showLikeAnimation} onAnimationEnd={() => setShowLikeAnimation(false)} />
          {/* {type === "video" ? (
            fileUrl !== "" && thumbnail !== "" ? (
              <VideoPlayer videoUrl={fileUrl!} thumbnailUrl={thumbnail!} />
            ) : (
              ""
            )
          ) : (
            <img className="postImg" src={fileUrl} alt={description} />
          )} */}
          <FeedMediaComponent
            description={description}
            fileUrl={fileUrl!}
            thumbnail={thumbnail!}
            type={type}
            imageClassName="postImg"
            isPrivate={isPrivate!}
            isMyProfile={isMyProfile!}
            profileName={name}
            username={username}
            feedId={feedId}
            isVideo={type === "video"}
          />

          {/* Hide bookmark for creators viewing other creators' content */}
          {!(authService.getRole() === CREATOR) && renderBookmarkIcon()}
          {isPrivate && (
            <div className="privateContentOverlay">
              <div className="privateContentText">
                <Row className="privateContentText">
                  <Typography.Text className="reserved-title">{t("reserved-for-subscribers")}</Typography.Text>
                </Row>
                <Row className="privateContentText">
                  <Typography.Text className="reserved-description">{t("reserved-for-subscribers-description")}</Typography.Text>
                </Row>
                {/* Hide subscribe button when both current user and creator are creators */}
                {!(authService.getRole() === CREATOR) && (
                  <Button
                    onClick={() => {
                      navigate(`/common/subscribe/${creatorId}`);
                    }}
                    type="link"
                    className="SubscriptionExtendBtn"
                  >
                    <div className="confirm-btn-background without-obligation">{t("Subscribe-without-obligation")}</div>
                  </Button>
                )}
              </div>
            </div>
          )}

          {isPrivate && (
            <div
              className="privateContentBottomOverlay"
              onClick={() => {
                navigate(`/common/subscribe/${creatorId}`);
              }}
            >
              <Typography.Text className="unlockText">{t("unlock-content-name", { name })}</Typography.Text>
              <img src={ChevronRight} alt="chevron-right" className="chevronIcon" />
            </div>
          )}
        </div>
        <CreatorDeletePostModal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} selectedFeed={feedId} />
        <OptionsModalComponent
          reportBtnLabel={t("report-feed")}
          isOpen={isOptionsModalOpen}
          onClose={closeOptionsModal}
          onReportClick={onReportClick}
        />
        <ReportModal isOpen={isReportModalOpen} target="feed" targetId={feedId!} onClose={closeReportModal} />
      </div>

      <div className="slideItemFooter">
        <Space className="mb-6">
          {likes ? (
            <img className="slideItemLike cursor-pointer" src={LikeGradientIcon} alt="likeFill" onClick={onLikeClick} />
          ) : (
            <img className="slideItemLike cursor-pointer" src={LikeIcon} alt="like" onClick={onLikeClick} />
          )}
          <Typography.Text className="slideItemLikeLabel">
            {t("liked-by")}
            <Typography.Text className="slideItemLikeSubLabel">
              {postTotalLikes} {`${t("person")}${totalLikes! > 1 ? "s" : ""}`}
            </Typography.Text>
          </Typography.Text>
        </Space>
        <br />
        <Typography.Text className="slideItemLikeLabel">
          <span onClick={() => navigate(`/p/${username}`)}>{name}</span>
          <ReadMore description={processDescription(description, creatorIds)} />
        </Typography.Text>
      </div>
    </div>
  );
};

export default MainSwiperSlideItemV2;
